// Hex grid (8% ink, toggleable) and the dev debug overlay (hex coordinates + elevation).
// Drawn every frame straight onto the screen canvas: cheap, and always crisp.
import type { MapData } from '../../sim/types';
import { centerOfOffset, cornerOfOffset, type Bounds } from '../hexgeom';
import type { Ink } from '../theme';

export const GRID_ALPHA = 0.08;

/** Offset-coordinate range of hexes that touch the visible world rectangle. */
export function visibleRange(
  map: MapData,
  view: Bounds,
): { c0: number; c1: number; r0: number; r1: number } {
  return {
    c0: Math.max(0, Math.floor((view.minX - 1) / 1.5)),
    c1: Math.min(map.width - 1, Math.ceil((view.maxX + 1) / 1.5)),
    r0: Math.max(0, Math.floor(view.minY / Math.sqrt(3)) - 1),
    r1: Math.min(map.height - 1, Math.ceil(view.maxY / Math.sqrt(3)) + 1),
  };
}

export function drawGrid(
  ctx: CanvasRenderingContext2D,
  map: MapData,
  view: Bounds,
  cssPx: number,
  ink: Ink,
): void {
  const { c0, c1, r0, r1 } = visibleRange(map, view);
  ctx.save();
  ctx.globalAlpha = GRID_ALPHA;
  ctx.strokeStyle = ink.ink;
  ctx.lineWidth = 1 * cssPx;
  ctx.lineJoin = 'round';
  ctx.beginPath();
  for (let r = r0; r <= r1; r++) {
    for (let c = c0; c <= c1; c++) {
      for (let i = 0; i <= 6; i++) {
        const p = cornerOfOffset(c, r, i % 6);
        if (i === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      }
    }
  }
  ctx.stroke();
  ctx.restore();
}

/** Dev flavor only: "col,row" (as in level files) and a big elevation digit per hex. */
export function drawDebug(
  ctx: CanvasRenderingContext2D,
  map: MapData,
  view: Bounds,
  cssPx: number,
  ink: Ink,
): void {
  const hexPx = 1 / cssPx; // hex circumradius in CSS px
  if (hexPx < 14) return; // too small to read
  const { c0, c1, r0, r1 } = visibleRange(map, view);
  ctx.save();
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = ink.ink;
  ctx.strokeStyle = ink.paper;
  ctx.lineJoin = 'round';
  const big = Math.min(18, hexPx * 0.5) * cssPx;
  const small = Math.min(11, hexPx * 0.3) * cssPx;
  for (let r = r0; r <= r1; r++) {
    for (let c = c0; c <= c1; c++) {
      const p = centerOfOffset(c, r);
      const cell = map.cells[r * map.width + c];
      if (!cell) continue;
      ctx.globalAlpha = 0.85;
      ctx.lineWidth = 3 * cssPx;
      ctx.font = `700 ${big}px ${ink.font}`;
      ctx.strokeText(String(cell.elev), p.x, p.y - 0.12);
      ctx.fillText(String(cell.elev), p.x, p.y - 0.12);
      if (hexPx >= 26) {
        ctx.globalAlpha = 0.7;
        ctx.font = `400 ${small}px ${ink.font}`;
        ctx.strokeText(`${c},${r}`, p.x, p.y + 0.42);
        ctx.fillText(`${c},${r}`, p.x, p.y + 0.42);
      }
    }
  }
  ctx.restore();
}
